<template>
	<div>
		<DetailsLayout :loading="isLoading" class="resource-details">
			<!-- Common back button in place of superTitle -->
			<template #super-title>
				<div class="back-button" v-if="!isLoading">
					<Button link @click="router.go(-1)">
						<ChevronIcon class="back-button-icon" type="regular" left />
						<span v-text="integrationName || 'Integrations'" />
					</Button>
				</div>
			</template>

			<!-- Custom title with inline sync button -->
			<template #title>
				<div class="title-with-sync" v-if="!isLoading">
					<h1 class="details-title" :title="resource?.label">
						{{ resource?.label }}
					</h1>
					<Button
						primary
						@click="syncResource"
						:loading="isSyncing"
						:disabled="isSyncDisabled"
						class="sync-button"
					>
						{{ t('admin_integrations_sync') }}
					</Button>
				</div>
			</template>

			<!-- Integration-specific content -->
			<template #content>
				<div class="resource-info-container" v-if="!isLoading">
					<div
						class="resource-info-item"
						v-for="item in resourceInfoItems"
						:key="item.id || item.label"
					>
						<div class="info-list-label">{{ item.label }}:</div>
						<div class="info-list-value">
							{{ item.value }}
						</div>
					</div>
				</div>
				<div v-else>
					<SkeletonBar class="content-skeleton"></SkeletonBar>
				</div>
			</template>

			<template #table>
				<div class="table-wrapper">
					<div class="table-header">
						{{ t('admin_integrations_sync_history') }}
					</div>
					<Table
						class="resources-table"
						:columns="columns"
						:rows="syncHistory"
						clickable
						infinite
						closePopoversOnScroll
						:loadingOverlay="fetchingSyncHistory"
						:defaultSort="sort"
						:error="fetchSyncHistoryError"
						headerSize="small"
						@sort="onSort"
						@retry="fetchSyncHistory"
						height="calc(100vh - 360px)"
						ref="table"
						@load-more="loadMore"
						:loadingMore="loadingMore"
					>
						<template #cell-updated_at="{ data }">
							{{ $m(data.updated_at).format('lll') }}
						</template>

						<template #cell-pronto_batch_id="{ data }">
							<Button
								v-if="data.pronto_batch_id"
								class="batch-link"
								link
								@click="gotoBatchDetails(data.pronto_batch_id)"
							>
								{{ data.pronto_batch_id }}
							</Button>
							<template v-else>--</template>
						</template>

						<template #cell-actions="{ data }">
							<Button
								v-if="data.error_message"
								link
								class="view-error-button"
								@click="errorDetails = data.error_message"
							>
								{{ t('admin_common_results_failure_view') }}
							</Button>
						</template>
					</Table>
				</div>
			</template>

			<!-- Integration-specific sidebar -->
			<template #sidebar>
				<component
					v-if="resourceDetailsComponent"
					:is="resourceDetailsComponent"
					:connection="connection"
					:configuration="configuration"
					:platform-configuration="platformConfiguration"
					:is-loading="isLoading"
					sidebar-only
					@delete="showDeleteModal = true"
				/>
			</template>
		</DetailsLayout>

		<StandardModal
			v-if="errorDetails"
			open
			small
			:title="t('common_error')"
			:confirmText="t('common_button_done')"
			:confirmProps="{ danger: true }"
			hideCancel
			@confirm="errorDetails = null"
			@cancel="errorDetails = null"
			class="sync-error-modal"
		>
			<div class="sync-error-wrapper">
				<CircleExclamationIcon type="regular" halo />
				<div class="sync-error-message" v-text="errorDetails" />
			</div>
		</StandardModal>
	</div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue'

// Import specific detail components
import {
	useAlert,
	Button,
	Table,
	ASC,
	SkeletonBar,
	StandardModal,
} from '@/components/common'
import { ChevronIcon, CircleExclamationIcon } from '@/icons'
import { useI18n } from 'vue-i18n'
import { integrationGet, integrationPut } from '@/api/integrations'
import { DetailsLayout } from '../common'
import { dateFormat } from '@/helperMethods'
import { useRouter } from 'vue-router'

const ALLOWED_SYNC_STATUSES = ['Completed', 'Completed with warnings', 'Failed']

const props = defineProps({
	connectionId: { type: [String, Number], required: true },
	resourceId: { type: [String, Number], required: true },
	integrationName: { type: String, required: false },
})

const { t } = useI18n()
const router = useRouter()
const alert = useAlert()
const isLoading = ref(false)
const resource = ref(null)
const fetchingResource = ref(false)
const fetchResourceError = ref(null)
const loadingMore = ref(false)
const sort = ref({ field: 'created_at', order: ASC, param: '-updated_at' })
const syncHistory = ref([])
const fetchingSyncHistory = ref(false)
const fetchSyncHistoryError = ref(null)
const apiResponse = ref(null)
const isSyncing = ref(false)
const pollingInterval = ref(null)
const errorDetails = ref(null)

const columns = ref([
	{
		field: 'updated_at',
		label: t('admin_integrations_date_time'),
		sorter: true,
		ascSort: 'updated_at',
		dscSort: '-updated_at',
	},
	{
		field: 'pronto_batch_id',
		label: t('admin_integrations_import_batch_id'),
		sorter: true,
		ascSort: 'pronto_batch_id',
		dscSort: '-pronto_batch_id',
	},
	{
		field: 'sync_status_label',
		label: t('admin_integrations_sync_status'),
		sorter: true,
		ascSort: 'sync_status_label',
		dscSort: '-sync_status_label',
	},
	{
		field: 'actions',
		align: 'right',
		width: '180px',
		minWidth: '180px',
	},
])

const resourceDetailsComponents = {
	// lti13: LTIResourceDetails,
	// // google_workspace: GoogleWorkspaceConnectionDetails,
	// // Add more as needed
}

// Computed
const resourceDetailsComponent = computed(() => {
	return null // TODO: Add resource details component based on integration type
})
// const resourceDetailsComponent = computed(() => {
// 	// Don't render any component until we have resource data
// 	if (!resource.value?.integration) {
// 		return null
// 	}

// 	const integrationType =
// 		connection.value?.integration?.type ||
// 		connection.value?.integration?.key ||
// 		'default'

// 	return resourceDetailsComponents[integrationType] || LTIConnectionDetails
// })

const resourceInfoItems = computed(() => [
	{
		label: t('admin_integrations_last_sync_time'),
		value:
			dateFormat(resource.value?.last_sync_at, 'MM/DD/YYYY, hh:mm A') || '--',
	},
	{
		label: t('admin_integrations_last_sync'),
		value: resource.value?.sync_status_label || '--',
	},
	{
		label: t('admin_integrations_next_sync_time'),
		value:
			dateFormat(resource.value?.next_sync_at, 'MM/DD/YYYY, hh:mm A') || '--',
	},
	{
		label: t('admin_integrations_status'),
		value: resource.value?.status_label || '--',
	},
])

const isSyncDisabled = computed(() => {
	const currentStatus = resource.value?.sync_status_label
	return !ALLOWED_SYNC_STATUSES.includes(currentStatus)
})

const startPolling = () => {
	if (pollingInterval.value) {
		clearInterval(pollingInterval.value)
	}

	pollingInterval.value = setInterval(async () => {
		await fetchResource()
		await fetchSyncHistory()

		if (ALLOWED_SYNC_STATUSES.includes(resource.value?.sync_status_label)) {
			stopPolling()
		}
	}, 20000) // 20 seconds
}

const stopPolling = () => {
	if (pollingInterval.value) {
		clearInterval(pollingInterval.value)
		pollingInterval.value = null
	}
}

watch(
	() => resource.value?.sync_status_label,
	(newStatus, oldStatus) => {
		if (!newStatus) {
			if (oldStatus && pollingInterval.value) {
				return
			}
			stopPolling()
			return
		}

		if (!ALLOWED_SYNC_STATUSES.includes(newStatus)) {
			startPolling()
		} else {
			stopPolling()
		}
	}
)

// Methods
const fetchResource = async () => {
	fetchingResource.value = true
	try {
		resource.value = (
			await integrationGet(
				`/connections/${props.connectionId}/resources/${props.resourceId}`
			)
		).data
	} catch (e) {
		console.error(e)
		alert.error(t('common_api_error_save'))
		fetchingResource.value = false
	} finally {
		fetchingResource.value = false
	}
}

const onSort = (newSort, { ascSort, dscSort }) => {
	sort.value = {
		...newSort,
		param: newSort.order === ASC ? ascSort : dscSort,
	}
	fetchSyncHistory()
}

const loadMore = async () => {
	if (!apiResponse.value?.next) return
	loadingMore.value = true
	try {
		apiResponse.value = await apiResponse.value.next()
		syncHistory.value = syncHistory.value.concat(apiResponse.value.data)
		loadingMore.value = false
	} catch (e) {
		console.error(e)
		loadingMore.value = false
		alert.error(t('common_api_error_save'))
	}
}

const fetchSyncHistory = async (payload = {}) => {
	payload.sort = sort.value.param
	try {
		fetchingSyncHistory.value = true
		apiResponse.value = await integrationGet(
			`/connections/${props.connectionId}/resources/${props.resourceId}/history`,
			payload
		)
		syncHistory.value = apiResponse.value.data
	} catch (error) {
		fetchSyncHistoryError.value = error
	} finally {
		fetchingSyncHistory.value = false
	}
}

const syncResource = async () => {
	isSyncing.value = true
	try {
		await integrationPut(
			`/connections/${props.connectionId}/resources/${props.resourceId}/sync`
		)

		// BLOAT: For some reason the sync doesn't return the updated data right away, so we need to wait 500ms before fetching the updated data or it will fetch and still show the old data.
		await new Promise((resolve) => setTimeout(resolve, 1000))

		await fetchResource()
		await fetchSyncHistory()

		if (!ALLOWED_SYNC_STATUSES.includes(resource.value?.sync_status_label)) {
			startPolling()
		}
	} catch (error) {
		console.error('Sync failed:', error)
		alert.error('Failed to sync resource')
	} finally {
		isSyncing.value = false
	}
}

const gotoBatchDetails = (id) => {
	const routeData = router.resolve({
		name: 'import',
		params: { id: id },
	})
	window.open(routeData.href, '_blank')
}

onMounted(async () => {
	isLoading.value = true
	fetchingResource.value = true
	await fetchResource()
	fetchingSyncHistory.value = true
	await fetchSyncHistory()
	fetchingSyncHistory.value = false
	isLoading.value = false
})

onBeforeUnmount(() => {
	stopPolling()
})
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.resource-details {
	:deep(.details-header) {
		.details-header-content {
			flex: 1; // Make the content area take all available space
			width: 100%;
		}
	}

	:deep(.content-skeleton) {
		height: 120px;
		width: 100%;
	}

	:deep(.table-container .table-row .table-cell) {
		padding-left: 25px !important;
	}

	:deep(.table-container .table-row .table-cell:last-child) {
		padding-right: 25px !important;
	}
	:deep(.table-row) {
		cursor: default !important;
		&:hover {
			background: none !important;
		}
	}

	.back-button {
		margin-bottom: 10px;

		.pronto-btn {
			font-size: 14px;
			padding: 0;
			height: auto;
		}

		.back-button-icon {
			font-size: 12px;
		}
	}

	.resource-info-container {
		padding-top: 16px;
		display: flex;
		flex-direction: column;
		gap: 12px;
		align-items: flex-start;

		.resource-info-item {
			display: inline-flex;
			justify-content: center;
			align-items: center;
			gap: 6px;
			font-family: Avenir-Roman;
			font-size: 14px;
			color: var(--neutral-800);

			.info-list-label {
				font-size: 11px;
				color: var(--neutral-600);
				text-transform: uppercase;
			}
		}
	}

	.table-wrapper {
		padding-top: 11px;
		.table-header {
			font-size: 24px;
			font-family: Avenir-Heavy;
			color: var(--neutral-800);
			margin-bottom: 20px;
			padding-left: 24px;
		}
		.resources-table {
			margin-top: 15px;
		}
		.search-bar-container {
			padding-right: 24px;
			padding-left: 24px;
		}
		.batch-link {
			padding: 0;
		}
	}

	.title-with-sync {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		gap: 16px; // Add explicit gap for separation

		.details-title {
			font-size: 36px;
			color: var(--neutral-800);
			font-family: Avenir-Heavy;
			margin: 0;
			flex: 1;
			min-width: 0; // Prevent flex overflow
			@include clamp(2);
		}

		.sync-button {
			flex-shrink: 0;
			margin-left: auto; // Ensure it's pushed to the right
		}
	}
}

// triple selector to outrank Button's own .pronto-btn.link color
.view-error-button.pronto-btn.link {
	padding: 0;
	color: var(--red-200);

	&:not(.disabled):hover {
		color: var(--red-300);
	}
}

.sync-error-wrapper {
	@include flex-column-center(16px);
	justify-content: center;
	height: 100%;
	padding: 24px 32px;
	text-align: center;

	.pronto-icon {
		font-size: 50px;
		color: var(--red-200);
	}

	.sync-error-message {
		font-family: Avenir-Roman;
		font-size: 14px;
		color: var(--neutral-800);
		word-break: break-word;
	}
}
</style>
